import { GeneralParameterDefinition, PersonalParameterDefinition, CareerData } from '../types';

/**
 * Get today's date in YYYY-MM-DD format
 */
const getTodayDateString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * General parameter definitions for the simulation
 */
export const generalParametersData: GeneralParameterDefinition[] = [
  {
    id: 'sim-start-date',
    name: 'Start Date',
    type: 'date',
    defaultValue: getTodayDateString(),
    description: 'The date to be considered &quot;Day #1&quot; by the simulator. Today is the default.'
  },
  {
    id: 'inflation-rate',
    name: 'Inflation Rate',
    type: 'meanStdDev',
    defaultMean: 3.71,    // 3.71 and 2.73 are the mean and standard deviation of the inflation rate from 1975 to 2024
    defaultStdDev: 2.73,
    unit: '%',
    description: 'How much the value of money is expected to decrease (positive values) or decrease (negative values) each year. The default mean (average) of 3.71% with a standard deviation of 2.73 is based on the US inflation rate from 1975 to 2024.'
  },
  {
    id: 'initial-balance',
    name: 'Initial Investments Balance',
    type: 'meanStdDev',
    defaultMean: 80000,   // American median is $87,000 (2025)
    defaultStdDev: 0,
    unit: '$',
    description: 'How much your retirement investment accounts are worth on day 1 of the simulation.'
  },
  {
    id: 'invest-pre-ret-apr',
    name: 'Investments Pre-Retirement APR',
    type: 'meanStdDev',
    defaultMean: 7.0,
    defaultStdDev: 3.0,
    unit: '% / year',
    description: 'Up until everyone in your family has retired, the expected annual rate of return on your investments. This is an approximation for how well you expect your investments may perform when invested in earlier, more aggressive investments (eg stocks more than bonds).'
  },
  {
    id: 'invest-post-ret-apr',
    name: 'Investments Post-Retirement APR',
    type: 'meanStdDev',
    defaultMean: 4.5,
    defaultStdDev: 0.5,
    unit: '% / year',
    description: 'Once everyone in your family has retired, the expected annual rate of return on your investments. This is an approximation for how well you expect your investments may perform when invested in more conservative, retirement-appropriate investments (eg bonds).'
  },
  {
    id: 'cost-of-living-retirement',
    name: "Needed Income at Retirement (Today's $)",
    type: 'meanStdDev',
    defaultMean: 80000,
    defaultStdDev: 10000,
    unit: '$ / year',
    description: 'How much annual income you expect to need when the first member of your family begins retirement, as calculated in today&apos;s dollars. You can find calculators out there that can help you estimate this number based on your current income and future retirement expectations, like how much you plan to travel, or fun stuff like how much you&apos;ll spend on healthcare. A crude approximation is to multiply your current annual income by 75%.'
  },
  {
    id: 'annual-need-decline',
    name: 'Need Decline During Retirement',
    type: 'meanStdDev',
    defaultMean: 1.0,
    defaultStdDev: 0.2,
    unit: '% / year',
    description: 'By what percentage you expect your annual income need to decrease each year during retirement. In reality, you may need to spend more in the early years of retirement due to travel or health issues or who-knows-what. But generally over time your spending needs may decline as you age. (You will probably party less at age 90 than at age 70&mdash;though who knows?) This value lets you model how you think those costs may change over time.'
  },
  {
    id: 'per-person-need-proportion',
    name: 'Per-Person Need Proportion',
    type: 'meanStdDev',
    defaultMean: 20.0,
    defaultStdDev: 10.0,
    unit: '%',
    description: 'What percentage of your total annual retirement need is attached to the life of each person? In other words, if there are more than one of you in the family and one of you dies, how much would household spending go down?'
  }
];

/**
 * Personal parameter definitions for individuals
 */
export const personalParametersData: PersonalParameterDefinition[] = [
  {
    id: 'birthdate',
    name: 'Birthdate',
    type: 'date',
    defaultValue: '1990-01-01',
    description: 'When this person was born.'
  },
  {
    id: 'currentAge',
    name: 'Current Age',
    type: 'calculated',
    unit: 'years',
    dependsOn: ['birthdate', 'sim-start-date'],
    description: 'This person&apos;s current age, based on the simulation start date and their birthdate.'
  },
  {
    id: 'retirementAge',
    name: 'Retirement Age',
    type: 'number',
    defaultValue: 65,
    unit: 'years',
    description: 'The age at which this person plans to retire.'
  },
  {
    id: 'socialSecurityMonthlyBenefit',
    name: 'Expected Social Security Benefit (monthly)',
    type: 'number',
    defaultValue: 1500,
    unit: '$',
    description: 'The expected monthly Social Security benefit for this person. You can get this number from <em>ssa.gov</em>.'
  },
  {
    id: 'socialSecurityAnnualBenefit',
    name: 'Expected Social Security Benefit (annual)',
    type: 'calculated',
    unit: '$',
    dependsOn: ['socialSecurityMonthlyBenefit'],
    description: 'The expected annual Social Security benefit for this person as calculated from the monthly value.'
  },
  {
    id: 'lifeExpectancy',
    name: 'Life Expectancy',
    type: 'meanStdDev',
    defaultMean: 85,
    defaultStdDev: 15,
    unit: 'years',
    description: 'The fun question. To what age do you expect this person to live? In the US a 30-year old man will, on average, live to be about 80, a woman to 85. For the standard deviation a value of 15 years is a reasonable guess. Although it&apos;s not fun to think about dying, from a financial perspective the longer you live in retirement the more it will cost, so just shoot for &quot;realistic&quot; and let the model worry about the rest.'
  },
  {
    id: 'yearsInRetirement',
    name: 'Years in Retirement',
    type: 'calculated',
    unit: 'years',
    dependsOn: ['retirementAge', 'lifeExpectancy'],
    description: 'Given the age at which you plan to retire and your life expectancy, how many years you expect to live in retirement.'
  }
];

/**
 * Create a default career structure
 */
export const createDefaultCareer = (): CareerData => ({
  id: `career-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
  startAge: { mean: 25, stdDev: 2 },
  isStartNow: true,
  endAge: { mean: 67, stdDev: 1 },
  isEndAtRetirement: true,
  initialIncome: { mean: 60000, stdDev: 5000 },
  annualIncomeIncrease: { mean: 4.0, stdDev: 2.0 },
  incomeInvestment: { mean: 8.0, stdDev: 1.0 },
  employerMatch: { mean: 4.0, stdDev: 0 },
  otherInvestment: { mean: 0, stdDev: 0 },
  inflationAdjustedIncrease: false
});

/**
 * Career parameter field definitions for UI generation
 */
export const careerParameterDefinitions = [
  { id: 'initialIncome', label: 'Initial Income', unit: '$ / year', isPercent: false, description: 'Your expected annual pay when you start this career, in today&apos;s dollars.' },
  { id: 'annualIncomeIncrease', label: 'Income Increase', unit: '% / year', isPercent: true, description: 'How much you expect your income to increase each year, ignoring inflation.' },
  { id: 'incomeInvestment', label: 'Income Investment (%)', unit: '%', isPercent: true, description: 'What percentage of your annual income you will invest.' },
  { id: 'employerMatch', label: 'Employer Match (%)', unit: '%', isPercent: true, description: 'What percentage of your annual income you expect your employer will match.' },
  { id: 'otherInvestment', label: 'Additional Investment', unit: '$ / year', isPercent: false, description: 'Any additional dollar amount you plan to contribute each year while in this career.' }
] as const;
